/** @file Firebase bootstrap. Region pinned to europe-west3 (DSGVO). */
import { initializeApp } from "firebase/app";
import { getAuth as _fbGetAuth, connectAuthEmulator } from "firebase/auth";
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from "firebase/firestore";

/** @type {import("firebase/app").FirebaseApp | null} */
let _app = null;
/** @type {import("firebase/firestore").Firestore | null} */
let _db = null;
/** @type {import("firebase/auth").Auth | null} */
let _auth = null;
let _emulatorsConnected = false;

/**
 * Initialize Firebase app + Firestore with offline persistence + Auth.
 * No-op if environment variables are missing (useful for first-time setup).
 * @returns {{ app: import("firebase/app").FirebaseApp, db: import("firebase/firestore").Firestore, auth: import("firebase/auth").Auth } | null}
 */
export function initFirebase() {
  if (_app && _db && _auth) {
    return { app: _app, db: _db, auth: _auth };
  }

  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  const useEmulators = import.meta.env.VITE_USE_FIREBASE_EMULATORS === "1";

  if ((!apiKey || !projectId) && !useEmulators) {
    console.warn(
      "[firebase] Missing VITE_FIREBASE_* env vars — running without Firebase. " +
        "Fill .env.local from .env.example to enable persistence.",
    );
    return null;
  }

  _app = initializeApp({
    apiKey: apiKey ?? "demo-api-key",
    projectId: projectId ?? "handballplan-dev",
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? "",
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? "",
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? "",
    appId: import.meta.env.VITE_FIREBASE_APP_ID ?? "",
  });
  _db = initializeFirestore(_app, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager(),
    }),
  });
  _auth = _fbGetAuth(_app);

  console.info("[firebase] Initialized — region pinned to europe-west3 via project config.");

  if (useEmulators && !_emulatorsConnected) {
    try {
      connectAuthEmulator(_auth, "http://127.0.0.1:9099", { disableWarnings: true });
      connectFirestoreEmulator(_db, "127.0.0.1", 8080);
      _emulatorsConnected = true;
      console.info("[firebase] Emulators connected (auth:9099, firestore:8080)");
    } catch (err) {
      console.warn("[firebase] Failed to connect emulators", err);
    }
  }

  return { app: _app, db: _db, auth: _auth };
}

/**
 * Get the initialized Firestore instance.
 * @returns {import("firebase/firestore").Firestore | null}
 */
export function getDb() {
  return _db;
}

/**
 * Get the initialized Firebase Auth instance.
 * @returns {import("firebase/auth").Auth | null}
 */
export function getAuth() {
  return _auth;
}

/** @returns {boolean} */
export function isFirebaseReady() {
  return _app !== null && _db !== null && _auth !== null;
}
